import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import SubmitButton from '../shared/submit-button'
import HelpText from '@/components/Form/HelpText'
import { AuthPrevButton } from '../auth-modal/auth-prev-button'
import { AuthModalBody } from '../auth-modal/auth-modal-body'

import useAuth from '../../hooks/use-auth'
import { useAuthNavigation } from '../../hooks/use-auth-navigation'
import { getRefererFromSearch } from '../../utils/auth-return'
import { signupSchema } from '../../validations/auth-schemas'
import { AUTH_STEPS } from '../../constants/auth-steps'
import { buildSignupPayload } from '../../utils/build-signup-payload'
import { mapSignupServerErrors } from '../../utils/map-server-errors'
import {
  AuthCheckboxField,
  AuthTextField,
  FacebookLoginButton,
  Logo,
  Title,
  TurnstileField,
} from '../shared'

const hiddenAutofillTrapStyle = {
  position: 'absolute',
  opacity: 0,
  height: 0,
  width: 0,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
}

export default function SignupForm () {
  const {
    submitting,
    turnstileToken,
    signupEmail,
    responseFacebook,
    resetTurnstile,
  } = useAuth()
  const { goToLogin } = useAuthNavigation()

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      nick: '',
      email: '',
      password: '',
      cpassword: '',
      referalCode: '',
      acceptTerms: false,
      checkAge: false,
      newsletter: true,
    },
    mode: 'onChange',
  })

  useEffect(() => {
    const referalCode = getRefererFromSearch(window.location.search)
    if (referalCode) {
      setValue('referalCode', referalCode)
    }
  }, [setValue])

  const onSubmit = async (formValues) => {
    const data = await buildSignupPayload({ formValues, turnstileToken })

    signupEmail(data, (response) => {
      if (response?.data?.errors && !response?.data?.success) {
        const errorKeys = mapSignupServerErrors(response.data.errors, setError)
        if (errorKeys.length > 0) {
          resetTurnstile()
        }
      }
    })
  }

  const canSubmit = isValid && Boolean(turnstileToken) && !submitting

  return (
    <AuthModalBody enableAutofillStyles>
      {/* <AuthPrevButton onClick={goToLogin} /> */}
      <Logo src='/images/Pickwin.png' alt='Pickwin logo' />
      <Title>Crear Cuenta</Title>

      <FacebookLoginButton
        callback={responseFacebook}
        loading={submitting === 'facebook-loading'}
      />

      <form autoComplete='off' onSubmit={(event) => event.preventDefault()}>
        <input
          type='text'
          name='username'
          autoComplete='username'
          tabIndex={-1}
          aria-hidden='true'
          style={hiddenAutofillTrapStyle}
          readOnly
        />
        <input
          type='password'
          name='password'
          autoComplete='current-password'
          tabIndex={-1}
          aria-hidden='true'
          style={hiddenAutofillTrapStyle}
          readOnly
        />

      <AuthTextField
        control={control}
        name='nick'
        inputName='signup-nick'
        placeholder='Nombre de usuario'
        iconSrc='/images/Icono usuario.png'
        preventAutofill
        autoComplete='off'
        style={{ marginTop: '1em' }}
      />
      <AuthTextField
        control={control}
        name='email'
        inputName='signup-email'
        placeholder='Email'
        iconSrc='/images/Icono Mail.png'
        preventAutofill
        autoComplete='off'
      />
      <AuthTextField
        control={control}
        name='password'
        inputName='signup-password'
        placeholder='Contraseña'
        iconSrc='/images/Icono candado.png'
        type='password'
        preventAutofill
        autoComplete='new-password'
      />
      <AuthTextField
        control={control}
        name='cpassword'
        inputName='signup-cpassword'
        placeholder='Confirmar Contraseña'
        iconSrc='/images/Icono candado.png'
        type='password'
        preventAutofill
        autoComplete='new-password'
      />
      <AuthTextField
        control={control}
        name='referalCode'
        inputName='signup-referal'
        placeholder='Código de referido'
        iconSrc='/images/Icono codigo.png'
        preventAutofill
        autoComplete='off'
        style={{ marginBottom: '1em' }}
      />
      </form>

      <AuthCheckboxField
        control={control}
        name='acceptTerms'
        label='Acepto términos y condiciones del sitio'
      />
      <AuthCheckboxField
        control={control}
        name='checkAge'
        label='Certifico que soy mayor de 18 años'
      />
      <AuthCheckboxField
        control={control}
        name='newsletter'
        label='Acepto recibir comunicados de Pickwin'
        style={{ marginBottom: '0.6em' }}
      />

      <TurnstileField step={AUTH_STEPS.SIGNUP_FORM} />

      <SubmitButton
        loading={submitting === 'email-loading'}
        onClick={handleSubmit(onSubmit)}
        disabled={!canSubmit}
      >
        Crear cuenta
      </SubmitButton>

      <HelpText>
        ¿Ya tienes cuenta? <strong onClick={goToLogin}>Inicia sesión</strong>
      </HelpText>
    </AuthModalBody>
  )
}
